import { useCallback, useLayoutEffect, useRef } from "react";
import { Button } from "@ascleo/ui/components/ui/button";
import { Volume2, VolumeX, House, Siren } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEmergencySend, useEmergencySelector } from "../context";
import { emergencySelectors } from "../selectors";
import { useViewTitle } from "../../../context";
import { usePlatform } from "../../../../../platform/context";
import { PulseRingIcon } from "../../../components/PulseRingIcon";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import emergencyAlarmAudioUrl from "../../../../../assets/audio/Emergency-Notfall.mp3";

// Bundled by Vite so every host (web, Electron, Capacitor) receives the alarm asset.
const DEFAULT_EMERGENCY_AUDIO_URL = emergencyAlarmAudioUrl;

interface EmergencyViewStepProps {
  /** Optional custom audio URL for emergency sound */
  audioUrl?: string;
  /** Called after the patient returns from the alarm view to the start screen */
  onReturnToStart?: () => void;
}

function createEmergencyAudioElement(audioUrl: string): HTMLAudioElement {
  const audio = new Audio(audioUrl);
  audio.loop = true;
  audio.preload = "auto";
  audio.volume = 1;
  return audio;
}

function stopEmergencyAudio(audio: HTMLAudioElement | null): void {
  audio?.pause();
  if (audio) {
    audio.currentTime = 0;
  }
}

/**
 * Emergency view step: Display emergency alert with audio and controls.
 * One exit (the footer "Zurück" is hidden by the flow, KO-96), the call to
 * action as the large centred headline (KO-95, KO-96), white text on the dark
 * destructive ink for contrast.
 *
 * On mount:
 * - Saves current system volume
 * - Sets system volume to 100%
 * - Starts emergency audio loop
 *
 * On unmount:
 * - Restores previous system volume
 * - Stops audio
 */
export function EmergencyViewStep({
  audioUrl = DEFAULT_EMERGENCY_AUDIO_URL,
  onReturnToStart,
}: EmergencyViewStepProps) {
  const { t } = useTranslation();
  const platform = usePlatform();
  const send = useEmergencySend();
  // Every state carries a header title, also the emergency hero (KO-12).
  useViewTitle(t("self-service.emergency.label"));
  const isPlaying = useEmergencySelector(emergencySelectors.isAudioPlaying);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioSourceRef = useRef<string | null>(null);
  const prevVolumeRef = useRef<number | null>(null);
  const playbackRequestRef = useRef(0);

  const ensureAudio = useCallback(() => {
    if (!audioRef.current || audioSourceRef.current !== audioUrl) {
      stopEmergencyAudio(audioRef.current);
      audioRef.current = createEmergencyAudioElement(audioUrl);
      audioSourceRef.current = audioUrl;
    }
    return audioRef.current;
  }, [audioUrl]);

  const stopCurrentSound = useCallback(() => {
    stopEmergencyAudio(audioRef.current);
  }, []);

  const stopSound = useCallback(() => {
    playbackRequestRef.current += 1;
    stopCurrentSound();
  }, [stopCurrentSound]);

  const startLoop = useCallback(
    async (signal?: AbortSignal) => {
      const requestId = playbackRequestRef.current + 1;
      playbackRequestRef.current = requestId;

      try {
        const audio = ensureAudio();
        if (signal?.aborted) return null;

        audio.loop = true;
        audio.muted = false;
        audio.volume = 1;
        audio.currentTime = 0;
        await audio.play();

        if (signal?.aborted || playbackRequestRef.current !== requestId) {
          stopEmergencyAudio(audio);
          return null;
        }
        return audio;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return null;
        }
        frontdeskDiagnosticLogger.warn(
          "Emergency alarm playback did not start",
          {
            error,
            hasAudioUrl: Boolean(audioUrl),
          },
        );
        void reportOperationalError({
          action: "emergency.alarm-playback",
          severity: "error",
          source: "native",
          transport: "html-audio",
          component: "EmergencyViewStep",
          error,
          context: {
            hasBundledAudio: Boolean(audioUrl),
          },
        });
        return null;
      }
    },
    [audioUrl, ensureAudio],
  );

  const handleToggleSound = () => {
    if (isPlaying) {
      stopSound();
      send({ type: "AUDIO_STOPPED" });
      return;
    }

    void (async () => {
      const source = await startLoop();
      send({ type: source ? "AUDIO_STARTED" : "AUDIO_STOPPED" });
    })();
  };

  const restoreVolume = useCallback(async () => {
    if (prevVolumeRef.current !== null && platform.setSystemVolume) {
      try {
        await platform.setSystemVolume(prevVolumeRef.current);
      } catch (error) {
        void reportOperationalError({
          action: "emergency.volume-restore",
          severity: "warning",
          source: "native",
          component: "EmergencyViewStep",
          error,
        });
      }
    }
  }, [platform]);

  const handleReturnToStart = async () => {
    stopSound();
    send({ type: "AUDIO_STOPPED" });
    await restoreVolume();
    onReturnToStart?.();
  };

  // On mount: start audio, then save current volume and set to 100%.
  // On unmount: restore volume, stop audio
  useLayoutEffect(
    function manageEmergencyAudioLifecycle() {
      let isActive = true;
      const abortController = new AbortController();

      const setup = async () => {
        // Start before async volume bookkeeping so iOS keeps the user activation.
        const source = await startLoop(abortController.signal);
        if (isActive) {
          send({ type: source ? "AUDIO_STARTED" : "AUDIO_STOPPED" });
        }

        // Save current system volume and set to maximum volume for the alarm.
        if (platform.getSystemVolume && platform.setSystemVolume) {
          try {
            const currentVolume = await platform.getSystemVolume();
            if (!isActive) return;
            prevVolumeRef.current = currentVolume;
            await platform.setSystemVolume(100);
          } catch (error) {
            void reportOperationalError({
              action: "emergency.volume-prepare",
              severity: "warning",
              source: "native",
              component: "EmergencyViewStep",
              error,
            });
          }
        }
      };

      setup();

      return function stopEmergencyAudioOnUnmount() {
        isActive = false;
        abortController.abort();
        stopSound();

        // Restore previous system volume
        void restoreVolume();
      };
    },
    [platform, restoreVolume, send, startLoop, stopSound],
  );

  return (
    <div className="mx-auto flex min-h-full w-full max-w-5xl min-w-0 flex-col justify-center px-2 py-6">
      <section
        role="alert"
        className="relative flex w-full min-w-0 flex-col items-center gap-6 overflow-hidden rounded-3xl bg-destructive-ink p-8 text-center text-white md:p-12"
      >
        <PulseRingIcon icon={<Siren />} className="bg-white/20 text-white" />

        <div className="flex w-full flex-col items-center gap-4">
          <span className="text-base font-semibold tracking-[0.12em] uppercase">
            {t("self-service.emergency.title")}
          </span>
          <h2 className="m-0 mx-auto max-w-[24ch] text-center font-heading text-[clamp(30px,3.4vw,46px)] leading-[1.1] font-semibold text-balance">
            {t("self-service.emergency.headline")}
          </h2>
          <p
            data-testid="frontdesk-emergency-112-guidance"
            className="m-0 mx-auto max-w-[54ch] rounded-2xl bg-black/25 px-6 py-4 text-center text-xl leading-normal font-semibold"
          >
            {t("self-service.emergency.subtitle")}
          </p>
        </div>

        <div className="mt-2 flex w-full max-w-xl flex-col gap-3">
          <Button
            data-testid="frontdesk-emergency-toggle-sound-btn"
            type="button"
            size="2xl"
            variant="secondary"
            className="w-full"
            onClick={handleToggleSound}
          >
            {isPlaying ? (
              <VolumeX aria-hidden className="size-6" />
            ) : (
              <Volume2 aria-hidden className="size-6" />
            )}
            {isPlaying
              ? t("self-service.emergency.stop-sound")
              : t("self-service.emergency.play-sound")}
          </Button>

          <Button
            data-testid="frontdesk-emergency-back-btn"
            type="button"
            size="2xl"
            variant="outline"
            className="w-full border-2 border-white bg-transparent text-white hover:bg-white/15 hover:text-white"
            onClick={handleReturnToStart}
          >
            <House aria-hidden className="size-6" />
            {t("self-service.emergency.back-to-start")}
          </Button>
        </div>
      </section>
    </div>
  );
}
